import React, { useRef, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { Moment } from 'moment';

export interface SafeDatePickerProps {
  label: string;
  value: Moment | null;
  onChange: (value: Moment | null) => void;
  disabled?: boolean;
  minDate?: Moment;
  maxDate?: Moment;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
}

export function SafeDatePicker({
  label,
  value,
  onChange,
  disabled = false,
  minDate,
  maxDate,
  required = false,
  error: errorProp,
  helperText: helperTextProp,
  fullWidth = true,
}: SafeDatePickerProps): React.ReactElement {
  const touched = useRef(false);
  const [, forceRender] = useState(0);

  // Treat invalid Moment as null
  const safeValue = value && value.isValid() ? value : null;

  function handleChange(newValue: Moment | null): void {
    if (newValue === null || !newValue.isValid()) {
      onChange(null);
    } else {
      onChange(newValue);
    }
  }

  function handleClose(): void {
    touched.current = true;
    forceRender((n) => n + 1);
  }

  let computedError = false;
  let computedHelperText = '';

  if (errorProp !== undefined) {
    computedError = errorProp;
    computedHelperText = helperTextProp ?? '';
  } else if (touched.current && required && safeValue === null) {
    computedError = true;
    computedHelperText = `${label} is required`;
  }

  return (
    <DatePicker
      label={label}
      value={safeValue}
      onChange={handleChange}
      onClose={handleClose}
      disabled={disabled}
      minDate={minDate}
      maxDate={maxDate}
      slotProps={{
        textField: {
          fullWidth,
          required,
          error: computedError,
          helperText: computedHelperText,
        },
      }}
    />
  );
}